import { BRAND_SOURCE_KEY , BRAND_SITE_HOST} from "@/lib/brandConfig";
/**
 * Página /exclusive-ar — Mismo layout y detalles que /exclusive (FullscreenExclusive).
 * Video Mux + countdown + CTA; checkout con Mercado Pago (suscripción 19.999 ARS/mes "Membresía").
 *
 * Comportamiento igual a /exclusive:
 * - Sin aviso "activar sonido" (hideUnmutePrompt).
 * - Countdown global desde EXCLUSIVE_COUNTDOWN_END.
 * - Mismo playback ID desde EXCLUSIVE_PLAYBACK_ID.
 * CTA: en lugar de Stripe, redirige a Mercado Pago create-subscription.
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import { MembershipFloatingCta } from "@/components/MembershipFloatingCta";
import { AR_PLANS } from "@/config/arInvitacionPlans";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const FALLBACK_PLAYBACK_ID =
  (import.meta.env.VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID as string | undefined)?.trim() ||
  (import.meta.env.VITE_PREMIUM_MUX_ES_VIP as string | undefined)?.trim() ||
  "";
const PRICE_ARS_STANDARD = 19999;
const PRODUCT_NAME_STANDARD = "Membresía";
const SUCCESS_PATH = "/exclusive-ar/success";

const buildTimePlaybackId =
  (import.meta.env.VITE_EXCLUSIVE_PLAYBACK_ID as string)?.trim() ||
  FALLBACK_PLAYBACK_ID;

// CTA copy fijo (gestión en código; VIP = /exclusive-ar?tier=vip)
const CTA_LABEL_STANDARD = "Descubrir";
const CTA_SUBTITLE_STANDARD = "Ingresar al Canal Privado + Hablar conmigo";
const CTA_LABEL_VIP = "Unirme VIP";
const CTA_SUBTITLE_VIP = "Grupo exclusivo + Hablar conmigo";

function useCountdown(endAtIso: string | null) {
  const [remaining, setRemaining] = useState<{
    h: number;
    m: number;
    s: number;
  } | null>(null);

  useEffect(() => {
    if (!endAtIso || typeof endAtIso !== "string") return;
    const end = new Date(endAtIso).getTime();
    if (Number.isNaN(end)) return;

    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      if (diff <= 0) {
        setRemaining(null);
        return;
      }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setRemaining({ h, m, s });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endAtIso]);

  return remaining;
}

async function createMercadoPagoSubscription(
  priceArs: number,
  productName: string,
  productId: string,
  buyerEmail: string,
): Promise<{ success: true; init_point: string } | { success: false; error: string }> {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  const response = await fetch(`${base}/api/mercadopago/create-subscription`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      productId,
      productName,
      reason: productName,
      price: priceArs,
      currency: "ARS",
      country: "AR",
      successPath: SUCCESS_PATH,
      buyerEmail,
      metadata: {
        source: BRAND_SOURCE_KEY,
        site: BRAND_SITE_HOST,
        brand: BRAND_PERSON_NAME,
        type: "membresia_ar",
        flow: "exclusive_ar",
        product_name: productName,
        price_ars: priceArs,
      },
    }),
  });

  const data = await response.json();
  if (!response.ok || !data.init_point) {
    return { success: false, error: data.error || "Error al crear checkout" };
  }
  return { success: true, init_point: data.init_point };
}

export default function FullscreenExclusiveAr() {
  const [searchParams] = useSearchParams();
  const isVip = searchParams.get("tier") === "vip";
  const priceArs = isVip ? AR_PLANS.vip.price : PRICE_ARS_STANDARD;
  const productName = isVip ? AR_PLANS.vip.productName : PRODUCT_NAME_STANDARD;
  const productId = isVip ? "exclusive_ar_membresia_vip" : "exclusive_ar_membresia";

  const [playbackId, setPlaybackId] = useState(buildTimePlaybackId);
  const [countdownEnd, setCountdownEnd] = useState<string | null>(null);
  // Audit 2026-06-14: poster blur-up (coherente con DualCTA).
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [checkoutState, setCheckoutState] = useState<"idle" | "email" | "creating" | "redirecting" | "error">("idle");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const { setTemplateName } = useCurrentTemplate();
  const countdown = useCountdown(countdownEnd);

  const handleCTAClick = useCallback((_url: string) => {
    if (checkoutState !== "idle" && checkoutState !== "email") return;
    if (checkoutState === "idle") {
      setCheckoutState("email");
      setCheckoutError(null);
      return;
    }
    const trimmed = email.trim();
    if (!trimmed) {
      setCheckoutError("Ingresá tu email para continuar.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setCheckoutError("Ingresá un email válido.");
      return;
    }
    setCheckoutState("creating");
    setCheckoutError(null);
    createMercadoPagoSubscription(priceArs, productName, productId, trimmed)
      .then((result) => {
        if (result.success === true) {
          setCheckoutState("redirecting");
          window.location.href = result.init_point;
        } else {
          setCheckoutState("error");
          setCheckoutError(result.error);
        }
      })
      .catch(() => {
        setCheckoutState("error");
        setCheckoutError("Error de conexión. Intenta de nuevo.");
      });
  }, [checkoutState, email, priceArs, productName, productId]);

  useEffect(() => {
    setTemplateName(null);
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
    };
  }, []);

  useEffect(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    if (!base) return;
    fetch(`${base}/api/public-playback-ids`)
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (
          data: { exclusive?: string; exclusiveCountdownEnd?: string } | null,
        ) => {
          const id = data?.exclusive?.trim();
          if (id) setPlaybackId(id);
          const end = data?.exclusiveCountdownEnd?.trim();
          if (end) setCountdownEnd(end);
        },
      )
      .catch(() => {});
  }, []);

  return (
    <div className="w-full h-dvh max-h-dvh overflow-hidden bg-black relative">
      {/* Overlay: formulario de email (primer click en CTA) */}
      {checkoutState === "email" && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm space-y-4">
            <p className="text-white text-center text-lg">Ingresá tu email para continuar</p>
            <input
              type="email"
              placeholder="Tu email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setCheckoutError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleCTAClick("/exclusive-ar")}
              className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-white/50 focus:border-white/40 focus:outline-none"
            />
            {checkoutError && <p className="text-center text-sm text-red-300">{checkoutError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setCheckoutState("idle");
                  setEmail("");
                  setCheckoutError(null);
                }}
                className="flex-1 rounded-lg bg-white/20 px-4 py-2 text-white text-sm hover:bg-white/30"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleCTAClick("/exclusive-ar")}
                className="flex-1 rounded-lg bg-white px-4 py-2 text-black text-sm font-medium hover:bg-white/90"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Overlay de estado del checkout (creando / redirigiendo / error) */}
      {checkoutState === "creating" && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <p className="text-white text-lg">Preparando checkout...</p>
        </div>
      )}
      {checkoutState === "redirecting" && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <p className="text-white text-lg">Redirigiendo a Mercado Pago...</p>
        </div>
      )}
      {checkoutState === "error" && checkoutError && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <div className="text-center max-w-sm">
            <p className="text-white/90 mb-4">{checkoutError}</p>
            <button
              type="button"
              onClick={() => {
                setCheckoutState("idle");
                setCheckoutError(null);
              }}
              className="rounded-lg bg-white/20 px-4 py-2 text-white text-sm hover:bg-white/30"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Countdown global (igual que /exclusive) */}
      {countdown && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-xl bg-black/70 text-white text-center backdrop-blur-sm border border-white/20"
          aria-live="polite"
          aria-label={`Tiempo restante ${countdown.h}:${String(countdown.m).padStart(2, "0")}:${String(countdown.s).padStart(2, "0")}`}
        >
          <span className="text-xs uppercase tracking-wider text-white/80 block mb-0.5">
            Termina en
          </span>
          <span className="font-body text-lg font-bold tabular-nums tracking-tight">
            {String(countdown.h).padStart(2, "0")}:
            {String(countdown.m).padStart(2, "0")}:
            {String(countdown.s).padStart(2, "0")}
          </span>
        </div>
      )}

      <MuxPosterBackdrop
        key={`poster-${playbackId}`}
        playbackId={playbackId}
        fitMode="smartcrop"
        hidden={videoPlaying}
      />
      <MuxVideoPlayerWithCTA
        playbackId={playbackId}
        variant="fullscreen"
        ctaLabel={isVip ? CTA_LABEL_VIP : CTA_LABEL_STANDARD}
        ctaSubtitle={isVip ? CTA_SUBTITLE_VIP : CTA_SUBTITLE_STANDARD}
        hideUnmutePrompt
        ctaUrl="/exclusive-ar"
        onCTAClick={handleCTAClick}
        onPlay={() => setVideoPlaying(true)}
      />
      <MembershipFloatingCta />
    </div>
  );
}
